import {providerChannels} from "@hosttools/core/constant";
import {getCurrencySymbol} from "@hosttools/core/shared/utils/utils.listing";
import {eachDayOfInterval} from "date-fns";
import {sortBy, uniq} from "lodash";

import type {Listing, ListingChannel} from "../models/listing";
import {formatChannelPrices, type Price, type PriceChannelCustom} from "../models/price";

import {formatyyyyMMdd} from "./date";
import {fromLocalToZoned} from "./timezone";

export const getPriceChannels = (price: PriceChannelCustom, channels: ListingChannel[]) => {
    return channels.reduce<PriceChannelCustom[ListingChannel][]>((result, channel) => {
        const priceChannel = price[channel];
        if (priceChannel) {
            result.push(priceChannel);
        }
        return result;
    }, []);
};

type Keys = ["price", "priceFormatted"] | ["minNights", "minNights"] | ["price", "price"];
export const generateDateTextByKey = (
    priceList: Array<{[key in Keys[number]]?: string | number}>,
    ...[key, keyDisplay]: Keys
) => {
    const sortedList = uniq(
        sortBy(priceList, key).reduce<Array<string | number>>((result, price) => {
            const value = price?.[keyDisplay];
            if (typeof value !== "undefined") {
                result.push(value);
            }
            return result;
        }, [])
    );

    const first = sortedList[0];
    const last = sortedList.length > 1 ? sortedList[sortedList.length - 1] : undefined;

    if (typeof first === "undefined") {
        return "-";
    }

    if (typeof last === "undefined") {
        return `${first}`;
    }

    return `${first}-${last}`;
};

export type PricesData = Record<
    string,
    {
        prices: Price[];
        currency: string;
    }
>;

export function buildPricesData(
    listing: Listing,
    start: string,
    end: string,
    prices: Price[],
    values: Partial<Record<ListingChannel | "all", number>> | number | undefined,
    isOverrideAvailability: boolean,
    isBlockedDate: boolean | undefined
): PricesData {
    const dates = eachDayOfInterval({
        start: fromLocalToZoned(start, "UTC"),
        end: fromLocalToZoned(end, "UTC")
    }).map(formatyyyyMMdd);

    return {
        [listing._id]: {
            currency: listing.currency,
            prices: dates.reduce((acc, elem) => {
                const price = prices.find(price => price.airbnbDate === elem);
                if (price) {
                    const channelsValues = providerChannels.reduce((acc, channel) => {
                        const priceChannel = price[channel];
                        if (priceChannel) {
                            if (typeof values === "undefined") {
                                acc[channel] = {...priceChannel};
                                return acc;
                            }
                            if (typeof values === "number") {
                                acc[channel] = {
                                    ...priceChannel,
                                    priceOverride: undefined,
                                    price: values,
                                    currentPrice: values
                                };
                            } else {
                                const value = values[channel] ? values[channel] : values.all;
                                acc[channel] = {
                                    ...priceChannel,
                                    priceOverride: value,
                                    price: value,
                                    currentPrice: value
                                };
                            }
                        }
                        return acc;
                    }, {} as Price);
                    acc.push({
                        ...price,
                        ...channelsValues,
                        // how about to reset price where this value should be undefined?
                        priceOverride:
                            typeof values === "number" || typeof values === "undefined"
                                ? undefined
                                : values.all
                                ? values.all
                                : undefined,
                        availabilityOverride: isOverrideAvailability,
                        blocked: isBlockedDate
                    } as Price);
                }

                return acc;
            }, [] as Price[])
        }
    };
}

export function transformPricesData<T extends PricesData>(data: T) {
    return Object.keys(data).reduce((result, listingId) => {
        if (data[listingId]) {
            const value = data[listingId].prices.reduce((prices, item) => {
                prices[item.airbnbDate] = formatChannelPrices(
                    item,
                    getCurrencySymbol(data[listingId].currency)
                );
                return prices;
            }, {} as Record<string, PriceChannelCustom>);

            result[listingId] = value;
        }
        return result;
    }, {} as Record<string, Record<string, PriceChannelCustom>>);
}
